import React, { useState } from 'react'

import { Alert, Box, Link, Text } from '@factorialco/deprecated-design-system'
import {
  AccessForSelectFragment,
  LeaveEmployeeDataFragment,
  ResourceFieldDataFragment,
  TimeoffCurrentEmployeeDataFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { convertHoursToTimeFormat } from 'modules/core/lib/formatTime'
import hasFeature from 'modules/core/lib/hasFeature'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'
import { DelegateApproverSelector } from 'modules/approvals/components/DelegateApproverSelector'
import { useAccessApprovalFlows } from 'modules/approvals/hooks/useAccessApprovalFlows'
import FranceCalendarWarnings from 'modules/timeoff/components/FranceCalendarWarnings'
import Content from 'modules/timeoff/components/LeaveContent'
import LeaveInfo from 'modules/timeoff/components/LeaveInfo'
import useCustomFields from 'modules/timeoff/hooks/useCustomFields'
import useFetchStats from 'modules/timeoff/hooks/useFetchStats'
import useLabourUnits from 'modules/timeoff/hooks/useLabourUnits'
import allowances from 'modules/timeoff/store/collections/allowances'
import leaveHistories from 'modules/timeoff/store/collections/leaveHistories'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import Allowance from 'modules/timeoff/store/models/Allowance'
import type LeaveHistory from 'modules/timeoff/store/models/LeaveHistory'

import ActionButtons from './ActionButtons'
import useForms from './useForms'
import useLeaveData, {
  type leaveConfirmationFields as LeaveConfirmationDetails,
} from './useLeaveData'
import { useMaterializedApprovalFlowApproverCheck } from './useMaterializedApprovalFlowApproverCheck'
import useModalCopies from './useModalCopies'
import useUpdateLeave from './useUpdateLeave'
import EauStatusCard from 'modules/payroll_bundle/pages/IncidenceDetailPage/_LeaveInfo/EauStatusCard'

// TODO: This is crazy, this component is re-rendering hundreds of times
interface Props {
  leaveId: string
  onClose: () => void
  onChange?: () => void
  leaveYear?: number
  onReject: () => void
  fromCalendar?: boolean
  fromShifts?: boolean
  delegatedApprover: AccessForSelectFragment | null | undefined
  customResourceFieldsData: ResourceFieldDataFragment[]
  leaveEmployee: LeaveEmployeeDataFragment
  currentEmployee: TimeoffCurrentEmployeeDataFragment
  sickFolderId: string
  leaveCanManageLeaveDocuments: boolean
}

const EditLeaveModal = ({
  leaveYear,
  leaveId,
  onClose,
  onChange,
  onReject,
  fromCalendar = false,
  fromShifts = false,
  delegatedApprover,
  leaveEmployee,
  currentEmployee,
  customResourceFieldsData,
  sickFolderId,
  leaveCanManageLeaveDocuments,
}: Props) => {
  useAsyncData(() => {
    if (!fromCalendar) return Promise.resolve()

    return allowances.fetch()
  }, [fromCalendar])

  const year = useMemo(() => leaveYear || moment().year(), [leaveYear])
  const {
    leave,
    leaveConfirmationDetails,
    loading: leaveDataLoading,
    permissions,
    medicalLeave,
  } = useLeaveData({ leaveId, leaveEmployeeId: leaveEmployee.id })

  const { fetchStats } = useFetchStats()

  const { form, documentForm } = useForms({
    leave,
    effectiveOn: medicalLeave.medicalEffectiveOn,
  })

  const labourUnits = useLabourUnits({
    form,
    employeeTimeoffPolicyId: leaveEmployee.timeoffPolicy?.id,
  })

  const customFields = useCustomFields({
    customResourceFieldsData,
  })

  const { onSubmit, updateLoading } = useUpdateLeave({
    form,
    documentForm,
    getFieldsValues: customFields.getValues,
    leave,
    leaveEmployeeId: leaveEmployee.id,
    fetchStats,
    canUpdateLeave: permissions.update,
    onChange,
    sickFolderId,
  })

  const { title, description } = useModalCopies({
    form,
    leave,
    isCurrentEmployee: currentEmployee.id === leaveEmployee.id,
    timeoffManagerFullName: leaveEmployee.timeoffManager?.fullName ?? null,
    canApprove: permissions.approve,
  })

  const onFetchStats = useCallback(() => {
    fetchStats(leave, leaveEmployee.id)
  }, [leave.id])

  const [hasNewDocuments, setHasNewDocuments] = useState(false)

  const onDocumentDropped = () => {
    setHasNewDocuments(true)
  }

  const formLeaveId = form.get('leave_type_id').value

  const leaveType = leaveTypes.get(formLeaveId)

  const leaveIdAllowance = leaveType?.getAllowanceForEmployee(leaveEmployee.timeoffPolicy?.id)

  const {
    company: { subscription },
    access,
    canManageTimeoff,
  } = useCurrentSession()

  const { loading: loadingApprovalFlows, approvalFlows } = useAccessApprovalFlows(
    'timeoff',
    access.id
  )

  const {
    loading: isCurrentApproverLoading,
    isCurrentApprover,
    materializedApprovalFlowId,
    approvalFlowId,
    canApproveFromFlow,
  } = useMaterializedApprovalFlowApproverCheck({
    resourceId: leave.id,
    resourceType: 'Timeoff::Leave',
  })

  const isLoading =
    leaveDataLoading || updateLoading || isCurrentApproverLoading || loadingApprovalFlows

  const timeoffSupervisor =
    canManageTimeoff ||
    (leaveEmployee.timeoffSupervisedByCurrent ?? false) ||
    (leaveEmployee.supervisedByCurrent ?? false)

  const labourUnitsHours = labourUnits.label === Allowance.ALLOWANCE_TYPES.hours
  const hoursText = convertHoursToTimeFormat(labourUnits.value, true)
  const daysText = i18n.tp('time_off.days_with_years', {
    count: labourUnits.value,
  })
  const timeTaken = labourUnitsHours ? hoursText : daysText

  useEffect(() => {
    const isreviewTimeOffRequest = permissions.approve

    if (isreviewTimeOffRequest) {
      track(trackingEvents.timeoff.reviewTimeOffRequestStart, {
        from_shifts: fromShifts,
      })
    }
  }, [permissions.approve, fromShifts])

  const isSubmitDisabled = () => {
    const isDetailsRequired = leaveType?.attributes.details_required
    const hasLeaveDescription: string = form.get('description').value
    const halfDay = form.get('half_day').value
    const hourAmount = form.get('hours_amount_in_cents').value

    if (isDetailsRequired) {
      return isLoading || !((form.isDirty || hasNewDocuments) && hasLeaveDescription)
    }

    const absurdStartTime = !form.startTimeHasValue && !halfDay && !!hourAmount

    return isLoading || !(form.isDirty || hasNewDocuments) || absurdStartTime
  }

  const [leaveHistory, setLeaveHistory] = React.useState<LeaveHistory | null>(null)
  const [authorName, setAuthorName] = React.useState<string | null>(null)
  const [authorId, setAuthorId] = React.useState<string | null>(null)
  const [authorType, setAuthorType] = React.useState<'Access' | 'User' | 'Company' | null>(null)

  useEffect(() => {
    const fetchLeaveHistory = async () => {
      try {
        const fetchLeaveHistories = await leaveHistories.fetch({
          data: { leave_id: leave.id },
        })
        const data = fetchLeaveHistories ? fetchLeaveHistories[0] : null
        setLeaveHistory(data)
        setAuthorName(data.author_name)
        setAuthorId(data.author_id)
        setAuthorType(data.author_type)
      } catch (error) {}
    }
    fetchLeaveHistory()
  }, [leave.id])

  const canSeeRequester =
    leaveHistory !== null &&
    authorType === 'Access' && // NOTE: we can only handle authorType of Access for now
    (canManageTimeoff || authorId !== currentEmployee.access.id)

  const canDelegateApprover =
    !hasFeature('dev_disable_delegate_approval') &&
    ((currentEmployee._policies?.approveLeaves.can ?? false) || approvalFlows.length > 0)

  const rejectionReason = leave.attributes.reason

  const toDate = (dateString?: string | null | Date) =>
    dateString ? new Date(dateString) : undefined

  const isLeaveConfirmationDetails = (value: unknown): value is LeaveConfirmationDetails => {
    return (
      typeof value === 'object' &&
      value !== null &&
      'status' in value &&
      'confirmed_start_date' in value &&
      'confirmed_end_date' in value &&
      'updated_at' in value
    )
  }

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={onClose}>
      <Modal
        illustration="calendar"
        title={title}
        description={description}
        onSubmit={isLoading ? undefined : onSubmit}
        onClose={onClose}
        footer={
          !medicalLeave.isMedical &&
          (!permissions.readOnly || isCurrentApprover) && (
            <ActionButtons
              form={form}
              leave={leave}
              onFetchStats={onFetchStats}
              onReject={onReject}
              onChange={(...args) => {
                if (onChange) {
                  onChange(...args)
                }
              }}
              canApprove={permissions.approve}
              canUpdate={permissions.update}
              canRemove={permissions.remove}
              isSubmitDisabled={isSubmitDisabled()}
              isManagerOrAdmin={timeoffSupervisor}
              isCurrentApprover={!!isCurrentApprover}
              materializedApprovalFlowId={materializedApprovalFlowId}
              approvalFlowId={approvalFlowId}
              isLoading={isLoading}
              fromShifts={fromShifts}
              onClose={onClose}
              employeeId={leaveEmployee.id}
              canApproveFromFlow={canApproveFromFlow}
              hasPendingChanges={form.isDirty || hasNewDocuments}
            />
          )
        }
      >
        {leaveDataLoading ? (
          <Loading />
        ) : (
          <>
            {isLeaveConfirmationDetails(leaveConfirmationDetails) &&
              leaveConfirmationDetails.status !== 'failed_request' && (
                <Box paddingBottom="s12">
                  <EauStatusCard
                    status={leaveConfirmationDetails.status}
                    retryable={false}
                    confirmedStartDate={toDate(leaveConfirmationDetails.confirmed_start_date)}
                    confirmedEndDate={toDate(leaveConfirmationDetails.confirmed_end_date)}
                    updatedAt={toDate(leaveConfirmationDetails.updated_at)}
                  />
                </Box>
              )}
            <Content
              leaveTypes={leaveTypes}
              documentForm={documentForm}
              form={form}
              leave={leave}
              readOnly={permissions.readOnlyFields}
              year={year}
              customFieldsForms={customFields.forms}
              onDocumentDropped={onDocumentDropped}
              leaveTypeReadOnly={permissions.readOnlyFields}
              // canCreateEauRequestOauthToken={canCreateEauRequestOauthToken ?? false} // TODO: CHECK THIS. It comes from rebase.
              // canApplyForEAU={canApplyForEAU ?? false} // TODO: CHECK THIS. It comes from rebase.
              leaveConfirmationDetails={leaveConfirmationDetails}
              timeTaken={timeTaken}
              labourUnits={labourUnits}
              leaveEmployee={leaveEmployee}
              leaveCanManageLeaveDocuments={leaveCanManageLeaveDocuments}
            />
            <Box gap="s24">
              <Box gap="s8">
                {leaveIdAllowance && (
                  <FranceCalendarWarnings
                    allowance={leaveIdAllowance}
                    admin={timeoffSupervisor}
                    labourUnits={labourUnits}
                  />
                )}
                <LeaveInfo form={form} employeeId={leaveEmployee.id} />
                {canDelegateApprover && (
                  <DelegateApproverSelector
                    approverAccessId={delegatedApprover?.id}
                    category="timeoff"
                    employeeAccessId={currentEmployee.access.id}
                    field={form.get('delegated_approver_id')}
                    startsOn={form.get('start_on').value}
                    finishOn={form.get('finish_on').value}
                  />
                )}
                {medicalLeave.isMedical && (
                  <Alert color="warning">
                    <Text.Normal>
                      {i18n.t('time_off.medical_leave.not_editable_notice.description')}
                    </Text.Normal>
                    <Link.Href href={helpCenterUrl('new_request')} target="_blank">
                      {i18n.t('time_off.medical_leave.not_editable_notice.link')}
                    </Link.Href>
                  </Alert>
                )}
              </Box>
              <ApprovalsInformationBlock resourceId={leave.id} resourceType="Timeoff::Leave" />
              {rejectionReason && (
                <Text.Normal size="200" color="grey800" overflowWrap>
                  {i18n.t('time_off.rejection_reason')}: {rejectionReason}
                </Text.Normal>
              )}
              {canSeeRequester && (
                <Text.Normal size="300" color="grey800">
                  {i18n.t('time_off.requester_label')}: {authorName}
                </Text.Normal>
              )}
            </Box>
          </>
        )}
      </Modal>
    </ExpiredTrialChecker>
  )
}

export default observer(EditLeaveModal)
